import { useLatestRef } from "@appretio/shared";
import { useCallback, useEffect, useRef, useState } from "react";
import { useFeedbackApi } from "./FeedbackContext";
import {
  clearFeedbackRestore,
  readFeedbackRestore
} from "./feedbackRestoreRoute";
import {
  type FeedbackViewRestore,
  parseFeedbackViewRestore,
  TOP_BAR_KEY
} from "./feedbackViewRestore";
import type { StagedGridViews } from "./gridViewRestore";
import { onUserInput } from "./userInput";

/** The router location, typed structurally (no router dependency here). */
interface RestoreLocation {
  pathname: string;
  search: string;
  hash: string;
  state: unknown;
}

/** What a {@link FeedbackViewRestoreOptions.seed} reports back. */
export interface FeedbackViewSeedResult {
  /** Whether the page should load data once the seeded state has committed. */
  apply: boolean;
  /**
   * Whether the seed restored the context selection from the page's own view
   * block, so a top bar the size budget dropped lost nothing.
   */
  selectionRestored?: boolean;
}

/** Inputs for {@link useFeedbackViewRestore}. */
export interface FeedbackViewRestoreOptions {
  /** react-router's `useLocation()`. */
  location: RestoreLocation;
  /** react-router's `useNavigate()`. */
  navigate: Parameters<typeof clearFeedbackRestore>[0];
  /**
   * Writes the captured view into the page's state, through plain state
   * setters — never through a handler that saves or edits data. Returns
   * whether the page should load data once that state has committed: true
   * only when the report says the reporter had data on screen.
   */
  seed: (restore: FeedbackViewRestore) => boolean | FeedbackViewSeedResult;
  /**
   * The page's own Apply, which reads the seeded state. Latest closure each
   * render. Without it the restore only selects.
   */
  apply?: () => void;
  /**
   * Whether Apply can run yet. A page whose Apply needs options that load
   * after the seed (a version list, a store-status lookup) holds the Apply
   * here until they have.
   */
  readyToApply?: boolean;
  /**
   * What of the reporter's view the loaded options no longer offer (a deleted
   * version, a year the cube lost, a failed lookup), as a sentence. Set while
   * the banner is up, it becomes the banner's note and cancels a waiting
   * Apply: the restore never loads a substitute.
   */
  unavailable?: string | null;
}

/** What the page renders from the restore. */
export interface FeedbackViewRestoreResult {
  /**
   * The parsed restore, while its banner is up; null otherwise. Gate
   * deferred seeding (picks that wait for an option list) on it.
   */
  restore: FeedbackViewRestore | null;
  /**
   * Why the restore did not load the reporter's data although they had it on
   * screen (the page was not ready in time), or what of their view is no
   * longer available; null otherwise.
   */
  applyNote: string | null;
  /** Takes the banner down, and ends the restore. */
  dismiss: () => void;
}

/**
 * Rebuild a page's view, one time, from a feedback report in the router state.
 *
 * The restore lasts as long as its banner: until the user dismisses it or
 * leaves the page. While it is up, the page's deferred picks, the grid views
 * and the `unavailable` notes keep landing. Only the automatic Apply can be
 * cancelled earlier: `apply` runs on a render after `seed`, so its closure
 * reads the seeded state, unless user input outside the banner, the
 * `unavailable` sentence or the 30 s limit comes first. Input cancels it
 * silently, because the page then shows the user's own work; the limit says
 * so in `applyNote`.
 *
 * The page's route needs `RESTORES_FEEDBACK_VIEW_HANDLE` in its handle;
 * without it, `FeedbackRestoreNotice` also says restoring is not supported.
 */
export function useFeedbackViewRestore({
  location,
  navigate,
  seed,
  apply,
  readyToApply = true,
  unavailable = null
}: FeedbackViewRestoreOptions): FeedbackViewRestoreResult {
  const [restore, setRestore] = useState<FeedbackViewRestore | null>(null);
  const [pendingApply, setPendingApply] = useState(false);
  const [applyNote, setApplyNote] = useState<string | null>(null);
  // Set once: an `unavailable` sentence cancelled the waiting Apply.
  const [cancelledByUnavailable, setCancelledByUnavailable] = useState(false);
  const startedRef = useRef(false);
  const gridViews = useFeedbackApi()?.gridViews;
  const seedRef = useLatestRef(seed);
  const applyRef = useLatestRef(apply);
  const pendingApplyRef = useLatestRef(pendingApply);
  const stagedRef = useRef<StagedGridViews | null>(null);

  useEffect(() => {
    if (startedRef.current) return;
    const payload = readFeedbackRestore(location);
    if (!payload) return;
    startedRef.current = true;
    const parsed = parseFeedbackViewRestore(payload);
    // Cleared before the seed, so a seed that writes URL state (`?tab=`)
    // navigates last and wins.
    clearFeedbackRestore(navigate, location);
    const seeded = seedRef.current(parsed);
    const { apply: wantsApply, selectionRestored } =
      typeof seeded === "boolean" ? { apply: seeded } : seeded;
    const shown = selectionRestored
      ? {
          ...parsed,
          droppedBlocks: parsed.droppedBlocks.filter(
            (block) => block !== TOP_BAR_KEY
          )
        }
      : parsed;
    setRestore(shown);
    if (!wantsApply || !applyRef.current) return;
    if (shown.droppedBlocks.includes(TOP_BAR_KEY)) {
      // Apply would load the page's default selection, not the reporter's.
      setApplyNote(
        "The report did not capture the reporter's context selection, so their data was not loaded."
      );
    } else {
      setPendingApply(true);
    }
  }, [location, navigate, seedRef, applyRef]);

  // The keyed grids take the reporter's views back while the banner is up
  // on this page: leaving the page or dismissing the banner releases them.
  useEffect(() => {
    if (!restore || !gridViews) return;
    const staged = gridViews.stage(restore.grids, {
      awaitApply: pendingApplyRef.current
    });
    stagedRef.current = staged;
    return staged;
  }, [restore, gridViews, pendingApplyRef]);

  // The user can still Apply: the grid views wait for the next load.
  const endApply = useCallback(() => {
    setPendingApply(false);
    stagedRef.current?.applyDone();
  }, []);

  // No Apply will come: the grid views settle on the rows on screen.
  const giveUpApply = useCallback(() => {
    setPendingApply(false);
    stagedRef.current?.settleWithoutApply();
  }, []);

  useEffect(() => {
    if (!restore || !unavailable) return;
    if (pendingApplyRef.current) setCancelledByUnavailable(true);
    giveUpApply();
  }, [restore, unavailable, pendingApplyRef, giveUpApply]);

  useEffect(() => {
    if (!pendingApply || !readyToApply || unavailable) return;
    // applyDone before the Apply runs, so the Apply's own load settles the
    // grid views.
    endApply();
    applyRef.current?.();
  }, [pendingApply, readyToApply, unavailable, applyRef, endApply]);

  useEffect(() => {
    if (!pendingApply) return;
    return onUserInput(endApply);
  }, [pendingApply, endApply]);

  // The wait is bounded: past 30 s an Apply would land long after the user
  // started working on the page.
  useEffect(() => {
    if (!pendingApply) return;
    const timer = window.setTimeout(() => {
      giveUpApply();
      setApplyNote("The reporter's data could not be loaded automatically.");
    }, 30_000);
    return () => window.clearTimeout(timer);
  }, [pendingApply, giveUpApply]);

  const dismiss = useCallback(() => {
    endApply();
    setRestore(null);
  }, [endApply]);

  // From the current sentence, so a changed sentence keeps the "not loaded"
  // part and a sentence that goes away takes its note down. A waiting Apply
  // never runs while a sentence is set.
  const unavailableNote =
    restore && unavailable
      ? cancelledByUnavailable || pendingApply
        ? `${unavailable} The reporter's data was not loaded.`
        : unavailable
      : null;

  return { restore, applyNote: unavailableNote ?? applyNote, dismiss };
}
